import React, { useState } from "react";
import api from "../../hooks/api";
import { API_URLS } from "../../App";
import {
  TenantProviderConfig,
  UpsertProviderConfigRequest,
  PROVIDER_NAMES,
  PROVIDER_LABELS,
  ProviderName,
} from "../../model/SitePayment";
import { X } from "lucide-react";

interface Props {
  provider: TenantProviderConfig | null;
  onClose: (saved: boolean) => void;
}

const CREDENTIAL_FIELDS: Record<
  string,
  {
    apiKeyLabel: string;
    apiKeyPlaceholder: string;
    webhookLabel: string;
    webhookPlaceholder: string;
    extraFields?: { key: string; label: string; placeholder: string }[];
  }
> = {
  stripe: {
    apiKeyLabel: "Chave Secreta (Secret Key)",
    apiKeyPlaceholder: "sk_live_...",
    webhookLabel: "Segredo do Webhook",
    webhookPlaceholder: "whsec_...",
    extraFields: [
      {
        key: "publishable_key",
        label: "Chave Publicavel (Publishable Key)",
        placeholder: "pk_live_...",
      },
    ],
  },
  paypal: {
    apiKeyLabel: "Client ID",
    apiKeyPlaceholder: "client_id...",
    webhookLabel: "Client Secret",
    webhookPlaceholder: "client_secret...",
  },
  adyen: {
    apiKeyLabel: "API Key",
    apiKeyPlaceholder: "AQEyhmft...",
    webhookLabel: "HMAC Key (Webhook)",
    webhookPlaceholder: "hmac_key...",
  },
  ifthenpay: {
    apiKeyLabel: "IfthenPay Key",
    apiKeyPlaceholder: "ifthenpay_key...",
    webhookLabel: "Webhook Secret",
    webhookPlaceholder: "whsec_...",
  },
  easypay: {
    apiKeyLabel: "Chave de API (ApiKey)",
    apiKeyPlaceholder: "api_key...",
    webhookLabel: "Webhook Secret",
    webhookPlaceholder: "whsec_...",
  },
  eupago: {
    apiKeyLabel: "Chave de API",
    apiKeyPlaceholder: "api_key...",
    webhookLabel: "Webhook Secret",
    webhookPlaceholder: "whsec_...",
  },
  sequra: {
    apiKeyLabel: "Credenciais (MerchantRef|Username|Secret)",
    apiKeyPlaceholder: "merchant_ref|username|secret",
    webhookLabel: "Signature Secret (HMAC Webhook)",
    webhookPlaceholder: "signature_secret...",
  },
};

const PaymentProviderForm: React.FC<Props> = ({ provider, onClose }) => {
  const isEdit = !!provider;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [providerName, setProviderName] = useState<string>(
    provider?.provider_name || "",
  );
  const [isEnabled, setIsEnabled] = useState(provider?.is_enabled ?? true);
  const [environment, setEnvironment] = useState(
    provider?.environment || "test",
  );
  const [apiKey, setApiKey] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [publishableKey, setPublishableKey] = useState(
    provider?.publishable_key || "",
  );

  // Manual provider fields
  const [manualBankName, setManualBankName] = useState(
    provider?.manual_bank_name || "",
  );
  const [manualIban, setManualIban] = useState(provider?.manual_iban || "");
  const [manualBic, setManualBic] = useState(provider?.manual_bic || "");
  const [manualAccountHolder, setManualAccountHolder] = useState(
    provider?.manual_account_holder || "",
  );
  const [manualPayableTo, setManualPayableTo] = useState(
    provider?.manual_payable_to || "",
  );
  const [manualMailingAddress, setManualMailingAddress] = useState(
    provider?.manual_mailing_address || "",
  );
  const [manualTtlHours, setManualTtlHours] = useState(
    provider?.manual_ttl_hours ?? 72,
  );

  const isManual = providerName === "manual";
  const isAutoProvider =
    providerName === "stripe" ||
    providerName === "paypal" ||
    providerName === "adyen" ||
    providerName === "ifthenpay" ||
    providerName === "easypay" ||
    providerName === "eupago" ||
    providerName === "sequra";
  const credentialConfig = CREDENTIAL_FIELDS[providerName];

  const handleSave = async () => {
    const label = PROVIDER_LABELS[providerName as ProviderName] || providerName;
    const rotating =
      !isManual && (apiKey.length > 0 || webhookSecret.length > 0);
    const confirmed = window.confirm(
      rotating
        ? `Guardar novas credenciais para "${label}"? As chaves atuais são substituídas imediatamente e os pagamentos passam a usar as novas.`
        : `Guardar a configuração de "${label}"?`,
    );
    if (!confirmed) return;

    setSaving(true);
    setError(null);
    try {
      const body: UpsertProviderConfigRequest = {
        provider_name: providerName,
        is_enabled: isEnabled,
        environment,
      };

      if (apiKey) body.api_key = apiKey;
      if (webhookSecret) body.webhook_secret = webhookSecret;
      if (publishableKey) body.publishable_key = publishableKey;

      if (isManual) {
        body.manual_bank_name = manualBankName;
        body.manual_iban = manualIban;
        body.manual_bic = manualBic;
        body.manual_account_holder = manualAccountHolder;
        body.manual_payable_to = manualPayableTo;
        body.manual_mailing_address = manualMailingAddress;
        body.manual_ttl_hours = manualTtlHours;
      }

      await api.put(API_URLS.PAYMENT_PROVIDER_CONFIGS, body);
      onClose(true);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao guardar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold">
            {isEdit ? "Editar Fornecedor" : "Novo Fornecedor"}
          </h3>
          <button onClick={() => onClose(false)}>
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="bg-red-50 text-red-700 px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}

          <div className="select is-fullwidth">
            <select
              value={providerName}
              onChange={(e) => setProviderName(e.target.value)}
              disabled={isEdit}
            >
              <option value="">Selecionar...</option>
              {PROVIDER_NAMES.map((n) => (
                <option key={n} value={n}>
                  {PROVIDER_LABELS[n]}
                </option>
              ))}
            </select>
          </div>

          {providerName && (
            <div className="grid grid-cols-2 gap-4">
              <div className="select is-fullwidth">
                <select
                  value={environment}
                  onChange={(e) =>
                    setEnvironment(e.target.value as "live" | "test")
                  }
                >
                  <option value="test">Test</option>
                  <option value="live">Live</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isEnabled}
                  onChange={(e) => setIsEnabled(e.target.checked)}
                  className="w-4 h-4"
                />
                Ativo
              </label>
            </div>
          )}

          {isAutoProvider && credentialConfig && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {credentialConfig.apiKeyLabel}{" "}
                  {isEdit && (
                    <span className="text-gray-400 font-normal">
                      (deixe vazio para manter)
                    </span>
                  )}
                </label>
                <input
                  type="password"
                  className="input"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={
                    isEdit
                      ? provider?.api_key_masked || "****"
                      : credentialConfig.apiKeyPlaceholder
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {credentialConfig.webhookLabel}{" "}
                  {isEdit && (
                    <span className="text-gray-400 font-normal">
                      (deixe vazio para manter)
                    </span>
                  )}
                </label>
                <input
                  type="password"
                  className="input"
                  value={webhookSecret}
                  onChange={(e) => setWebhookSecret(e.target.value)}
                  placeholder={credentialConfig.webhookPlaceholder}
                />
              </div>
              {credentialConfig.extraFields?.map((field) => (
                <div key={field.key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label}
                  </label>
                  <input
                    type="text"
                    className="input"
                    value={
                      field.key === "publishable_key" ? publishableKey : ""
                    }
                    onChange={(e) => {
                      if (field.key === "publishable_key")
                        setPublishableKey(e.target.value);
                    }}
                    placeholder={field.placeholder}
                  />
                </div>
              ))}
            </>
          )}

          {isManual && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Banco
                </label>
                <input
                  type="text"
                  className="input"
                  value={manualBankName}
                  onChange={(e) => setManualBankName(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    IBAN
                  </label>
                  <input
                    type="text"
                    className="input"
                    value={manualIban}
                    onChange={(e) => setManualIban(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    BIC/SWIFT
                  </label>
                  <input
                    type="text"
                    className="input"
                    value={manualBic}
                    onChange={(e) => setManualBic(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Titular da Conta
                </label>
                <input
                  type="text"
                  className="input"
                  value={manualAccountHolder}
                  onChange={(e) => setManualAccountHolder(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pagavel a
                </label>
                <input
                  type="text"
                  className="input"
                  value={manualPayableTo}
                  onChange={(e) => setManualPayableTo(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Morada para Envio
                </label>
                <textarea
                  className="textarea"
                  rows={2}
                  value={manualMailingAddress}
                  onChange={(e) => setManualMailingAddress(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  TTL Horas (prazo para pagamento)
                </label>
                <input
                  type="number"
                  className="input"
                  value={manualTtlHours}
                  onChange={(e) =>
                    setManualTtlHours(parseInt(e.target.value) || 72)
                  }
                />
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t">
          <button
            className="button is-light is-small"
            onClick={() => onClose(false)}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            className="button is-primary is-small"
            onClick={handleSave}
            disabled={saving || !providerName}
          >
            {saving ? "A guardar..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentProviderForm;
